import React, { useEffect, useState, useRef } from 'react';
import 'bootstrap-select';
import FormService from 'service/FormService';
import FormGroupService from 'service/FormGroupService';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { hasFormAccess } from '../../helpers/hasPrivilege';
import { QRCodeCanvas } from 'qrcode.react';
import CustomDropdown from 'component/CustomDropdown';
import { PageSizeOption } from 'domain/constant/PageSizeOption';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';

const renderTypeBadge = (type?: string) => {
  const normalized = (type || '').toLowerCase();
  if (normalized === 'group') {
    return (
      <span
        className="badge d-inline-flex align-items-center gap-1 px-2.5 py-1"
        style={{
          backgroundColor: '#eef2ff',
          color: '#4f46e5',
          border: '1px solid #c7d2fe',
          borderRadius: '20px',
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.2px'
        }}
      >
        <i className="fa-solid fa-layer-group me-1" style={{ fontSize: '0.75rem' }}></i>
        Group
      </span>
    );
  }
  if (normalized === 'content') {
    return (
      <span
        className="badge d-inline-flex align-items-center gap-1 px-2.5 py-1"
        style={{
          backgroundColor: '#ecfdf5',
          color: '#059669',
          border: '1px solid #a7f3d0',
          borderRadius: '20px',
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.2px'
        }}
      >
        <i className="fa-solid fa-file-lines me-1" style={{ fontSize: '0.75rem' }}></i>
        Content
      </span>
    );
  }
  return (
    <span className="badge bg-light text-muted border px-2 py-1" style={{ borderRadius: '20px', fontSize: '0.75rem' }}>
      {type || '-'}
    </span>
  );
};

const Form: React.FC = () => {
  const { profile } = useSelector((state: any) => state.auth);
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);

  const { navRef: typeNavRef, indicatorStyle: typeIndicatorStyle } = useNavPillsIndicator(selectedType);

  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState<{ id: string; name: string; demoCode: string; testingUrl: string } | null>(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const addDropdownRef = useRef<HTMLDivElement>(null);

  const canCreate = hasFormAccess(profile, 'create');
  const canUpdate = hasFormAccess(profile, 'update');
  const canDelete = hasFormAccess(profile, 'delete');

  const formService = FormService()
  const formGroupService = FormGroupService()

  const fetchData = async () => {
    const token = profile?.data.token;
    if (!token) return;

    setLoading(true);
    try {
      const res = await formService.formList(token, searchTerm, currentPage, pageSize, selectedType);
      console.log(res)
      setData(res.data ?? []);
      setTotalPages(res.pagination?.totalPage ?? 0);
      setTotalRecords(res.pagination?.totalRecords ?? 0)
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, selectedType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleCloseQRModal();
      }
      if (addDropdownRef.current && !addDropdownRef.current.contains(event.target as Node)) {
        setShowAddDropdown(false);
      }
    };

    if (showQRModal || showAddDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showQRModal, showAddDropdown]);

  const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setCurrentPage(1);
    fetchData();
  };

  const handleDelete = async (id: string, type?: string) => {
    const token = profile?.data.token;
    if (!token) return;

    const confirmed = window.confirm("Are you sure want to delete this data?");
    if (confirmed) {
      setLoading(true);
      try {
        if ((type || '').toLowerCase() === 'group') {
          await formGroupService.formGroupDelete(token, id);
        } else {
          await formService.formDelete(token, id);
        }
        alert("Data successfully deleted");
        fetchData()
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleShowQR = (form: { id: string; name: string; demoCode: string; testingUrl: string }) => {
    if (!form.demoCode) {
      return;
    }
    setSelectedForm(form);
    setShowQRModal(true);
  };

  const handleCloseQRModal = () => {
    setShowQRModal(false);
    setSelectedForm(null);
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="d-flex">
            <h5 style={{ flex: 1 }}>Forms</h5>
            <div>
              <p>TransMore Forms</p>
            </div>
          </div>
        </div>
        <div className="col-12">

          <div className="p-4" style={{ background: 'white' }}>
            <div className="mb-3 d-flex flex-nowrap gap-3 align-items-center text-nowrap" style={{ overflow: 'visible' }}>

              <form className="d-flex" onSubmit={handleSearchClick}>
                <input
                  type="text"
                  className="form-control form-control-compact me-2"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 120 }}
                />
                <button type="submit" className="px-4 btn btn-primary">
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <div className="d-flex justify-content-end align-items-stretch gap-2" style={{ flex: 1 }}>
                <div ref={typeNavRef} className="nav-pills-animated">
                  <div
                    className="nav-pill-indicator"
                    style={typeIndicatorStyle}
                  />
                  <button
                    type="button"
                    className={`nav-link ${selectedType === '' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedType('');
                      setCurrentPage(1);
                    }}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`nav-link ${selectedType === 'group' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedType('group');
                      setCurrentPage(1);
                    }}
                  >
                    Group
                  </button>
                  <button
                    type="button"
                    className={`nav-link ${selectedType === 'content' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedType('content');
                      setCurrentPage(1);
                    }}
                  >
                    Content
                  </button>
                </div>

                {canCreate && (
                  <div className="position-relative d-flex align-items-stretch" ref={addDropdownRef}>
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center justify-content-between px-3 gap-2"
                      style={{ borderRadius: '8px', fontWeight: 500 }}
                      onClick={() => setShowAddDropdown(!showAddDropdown)}
                    >
                      <span className="d-flex align-items-center gap-1.5">
                        Add
                      </span>
                      <i
                        className="fa-solid fa-chevron-down ms-1"
                        style={{
                          fontSize: '0.75rem',
                          transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          transform: showAddDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}
                      ></i>
                    </button>

                    {showAddDropdown && (
                      <div
                        className="dropdown-menu show position-absolute end-0 shadow-lg border rounded-3 p-1.5 animate-pop-in"
                        style={{
                          minWidth: '220px',
                          zIndex: 1050,
                          top: 'calc(100% + 6px)',
                          display: 'block',
                          backgroundColor: '#ffffff',
                          borderRadius: '12px',
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)'
                        }}
                      >
                        <Link
                          to="/form-group/0"
                          className="dropdown-item d-flex align-items-center gap-2.5 px-3 py-2 rounded-2"
                          onClick={() => setShowAddDropdown(false)}
                          style={{ transition: 'background-color 0.15s ease' }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                            style={{ width: 32, height: 32, backgroundColor: '#eef2ff', color: '#4f46e5' }}
                          >
                            <i className="fa-solid fa-layer-group" style={{ fontSize: '0.85rem' }}></i>
                          </div>
                          <div>
                            <div className="fw-semibold text-dark" style={{ fontSize: '0.85rem', lineHeight: '1.2' }}>Group</div>
                            <small className="text-muted d-block mt-0.5" style={{ fontSize: '0.72rem' }}>Create Form Group</small>
                          </div>
                        </Link>
                        <Link
                          to="/form/0"
                          className="dropdown-item d-flex align-items-center gap-2.5 px-3 py-2 rounded-2 mt-1"
                          onClick={() => setShowAddDropdown(false)}
                          style={{ transition: 'background-color 0.15s ease' }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                            style={{ width: 32, height: 32, backgroundColor: '#ecfdf5', color: '#059669' }}
                          >
                            <i className="fa-solid fa-file-lines" style={{ fontSize: '0.85rem' }}></i>
                          </div>
                          <div>
                            <div className="fw-semibold text-dark" style={{ fontSize: '0.85rem', lineHeight: '1.2' }}>Content</div>
                            <small className="text-muted d-block mt-0.5" style={{ fontSize: '0.72rem' }}>Create Form Content</small>
                          </div>
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <table className="table table-condensed table-hover">
              <thead className="table-primary">
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Demo Code</th>
                  <th>Version</th>
                  <th>Created At</th>
                  <th>Created By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {!loading ? (
                  data.length > 0 ? (
                    data.map((item) => (
                      <tr key={item.id}>
                        <td className='table-hover'>{item.id}</td>
                        <td>{item.name}</td>
                        <td>{renderTypeBadge(item.type)}</td>
                        <td>
                          {item.demoCode ? (
                            <span>{item.demoCode}</span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>{item.version}</td>
                        <td>{item.createdAt}</td>
                        <td>{item.createdBy}</td>
                        <td>
                          <div className="d-flex">
                            {item.demoCode && (
                              <button
                                className="border-0 btn btn-outline-info btn-small-compact"
                                onClick={() => handleShowQR(item)}
                                title="Show QR Code"
                              >
                                <i className="fa-solid fa-qrcode"></i>
                              </button>
                            )}
                            {canUpdate && (
                              <Link to={(item.type === 'group' ? '/form-group/' : '/form/') + item.id}>
                                <button className="border-0 btn btn-outline-warning btn-small-compact ms-2">
                                  <i className="fa-solid fa-edit"></i>
                                </button>
                              </Link>
                            )}
                            {canDelete && (
                              <button
                                className="border-0 btn btn-outline-danger btn-small-compact ms-2"
                                onClick={() => handleDelete(item.id, item.type)}
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center">
                        Tidak ada data
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="mt-3 d-flex justify-content-end align-items-center">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={pageSize}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setPageSize(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                </span>

                <nav>
                  <ul className="mb-0 pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>

                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showQRModal && selectedForm && (
        <div
          className="modal show d-block"
          tabIndex={-1}
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050 }}
          onClick={handleCloseQRModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            role="document"
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="text-center p-4">
                <h5 className="mb-4">{selectedForm.name}</h5>
                <div className="d-flex justify-content-center p-4 rounded">
                  <QRCodeCanvas
                    id="qr-code-canvas"
                    value={selectedForm.demoCode}
                    size={200}
                    level="H"
                  />
                </div>
                <p className="fw-bold mb-4">Code: {selectedForm.demoCode}</p>
                <p className="fw-bold mb-2">Web Form Test</p>
                <a
                  href={selectedForm.testingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fw-bold mb-2 text-primary"
                  style={{
                    wordBreak: 'break-all',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                >
                  {selectedForm.testingUrl}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Form;